/**
 * Instance Utilities
 *
 * Single source of truth for instance extraction logic.
 * Used by useInstancesData to sync NavTree data to the instances store.
 */

import type { JsxElement } from "@/utils/jsx/jsxElementTypes";
import type { NavTreeNode, Storyboard } from "@/utils/types";
import type { Instance } from "./instancesStore";

// ═══════════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════════

/** Data attribute used to track hot reload timestamps on component instances */
const HOT_RELOAD_TS_PROP = "data-tempo-hot-reload-ts";

// ═══════════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * NavTree lookup structure: storyboardId -> codebaseId -> NavTreeNode[]
 */
export type SbIdToCodebaseIdToNavNodeLookup = Record<
  string,
  Record<string, NavTreeNode[]>
>;

// ═══════════════════════════════════════════════════════════════════════════════
// INSTANCE MATCHING
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Finds all component-instance codebaseIds that match a component definition.
 *
 * A match occurs when:
 * 1. The element is a "component-instance" type
 * 2. The component name matches exactly
 * 3. The import filename matches (with flexible path matching)
 *
 * @param component - The component definition to match against
 * @param treeElementLookup - Map of codebaseId -> JsxElement from codebaseStore
 * @returns Array of codebaseIds for matching component instances
 */
export function findMatchingInstanceCodebaseIds(
  component: JsxElement,
  treeElementLookup: Record<string, JsxElement>,
): string[] {
  const componentFileWithoutExt =
    component.filename?.replace(/\.[^/.]+$/, "") || "";
  const componentName = component.componentName;

  if (!componentName) return [];

  const matchingIds: string[] = [];

  for (const element of Object.values(treeElementLookup)) {
    if (element.type !== "component-instance") continue;
    if (element.componentName !== componentName) continue;

    const importFilename = element.componentSourceImport?.filename;
    if (importFilename && componentFileWithoutExt) {
      // Flexible path matching: exact, ends-with, or contains
      if (
        componentFileWithoutExt === importFilename ||
        componentFileWithoutExt.endsWith(importFilename) ||
        importFilename.endsWith(componentFileWithoutExt)
      ) {
        matchingIds.push(element.codebaseId);
      }
    }
  }

  return matchingIds;
}

// ═══════════════════════════════════════════════════════════════════════════════
// INSTANCE EXTRACTION
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Extracts a minimal Instance from a NavTreeNode.
 *
 * Returns null if the navNode has no capturable DOM children (required for
 * screenshot capture and dimension measurement).
 *
 * @param navNode - The NavTreeNode representing the component instance
 * @param instanceCodebaseId - The codebaseId of this instance
 * @param storyboardId - The storyboard containing this instance
 * @param pageId - The page containing the storyboard
 * @param component - The component definition for metadata
 * @param instanceFilepath - The file where this instance is used (from treeElementLookup[instanceCodebaseId].filename)
 * @returns Instance object or null if not capturable
 */
export function extractInstanceFromNavNode(
  navNode: NavTreeNode,
  instanceCodebaseId: string,
  storyboardId: string,
  pageId: string,
  component: JsxElement,
  instanceFilepath: string,
): Instance | null {
  // Require children for DOM capture
  if (!navNode.children || navNode.children.length === 0) {
    return null;
  }

  // Collect DOM codebaseIds and find first capturable DOM path
  const domCodebaseIds: string[] = [];
  let firstChildDomUniquePath: string | undefined;

  for (const child of navNode.children) {
    const childCodebaseId = child.tempoElement?.codebaseId;
    if (childCodebaseId) {
      domCodebaseIds.push(childCodebaseId);
    }
    if (!firstChildDomUniquePath) {
      firstChildDomUniquePath =
        child.domUniquePath || child.domUniquePathForComponent;
    }
  }

  // Require at least one DOM element for capture
  if (domCodebaseIds.length === 0) {
    return null;
  }

  // Extract props, filtering out internal data attributes
  const props: Record<string, unknown> = {};
  let hotReloadTs: number | undefined;

  if (navNode.props) {
    for (const [propName, propData] of Object.entries(navNode.props)) {
      if (propName === HOT_RELOAD_TS_PROP) {
        hotReloadTs = Number(propData.value) || undefined;
        continue;
      }
      if (propName.startsWith("data-")) continue;
      props[propName] = propData.value;
    }
  }

  return {
    instanceCodebaseId,
    storyboardId,
    pageId,
    uniquePath: navNode.tempoElement?.uniquePath || "",
    domUniquePath: firstChildDomUniquePath || navNode.domUniquePath || "",
    componentName: component.componentName || "",
    componentFilepath: component.filename || "",
    instanceFilepath,
    componentExportName: component.defaultExport
      ? undefined
      : component.componentName,
    domCodebaseIds,
    props,
    hotReloadTs,
    contentWindowData: {},
    dragData: {},
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// STORYBOARD HELPERS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Flattens pages into storyboards with pageId hydrated.
 * Filters out deleted storyboards.
 */
export function flattenStoryboardsFromPages(
  pages: Array<{ id: string; storyboards?: Storyboard[] }>,
): Array<Storyboard & { pageId: string }> {
  return pages.flatMap((page) =>
    (page.storyboards || [])
      .filter((sb) => !sb.deleted)
      .map((sb) => ({ ...sb, pageId: page.id })),
  );
}
